/**
 * GovCidadaos — listagem de cidadãos do órgão (equivalente a Clientes
 * no privado). 100% do backend é compartilhado (`/api/customers/*`)
 * — o multi-tenant garante isolamento por tenant_id. Aqui trocamos
 * apenas TERMINOLOGIA (Cliente→Cidadão) e escondemos colunas
 * automotivas (veículo, marca, modelo).
 *
 * v0.99.9 · Wave 2 · Fev/2026
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  MagnifyingGlass, UserCircle, Plus, ShieldCheck, EnvelopeSimple, Phone,
  ArrowClockwise, IdentificationCard,
} from "@phosphor-icons/react";

export default function GovCidadaos() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [total, setTotal] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/customers/page", {
        params: { search, limit: 25, offset: 0 },
      });
      setRows(data?.items || []);
      setTotal(data?.total || 0);
    } catch (e) {
      toast.error("Falha ao carregar cidadãos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const debounced = useMemo(() => {
    let t;
    return (v) => {
      clearTimeout(t);
      t = setTimeout(() => { setSearch(v); }, 350);
    };
  }, []);

  useEffect(() => { load(); }, [search]);

  return (
    <div data-testid="gov-cidadaos-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-700/70 font-medium mb-1">
            Cadastros
          </div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900 tracking-tight flex items-center gap-2">
            <UserCircle size={22} className="text-emerald-700" />
            Cidadãos
          </h1>
          <p className="text-[12.5px] text-slate-500 mt-0.5">
            Cadastro consolidado dos cidadãos atendidos pelo órgão — com histórico, conformidade LGPD e canais.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-slate-500">
            <strong className="text-slate-700 tabular-nums">{total}</strong> cadastrados
          </span>
          <button
            onClick={load}
            data-testid="cidadaos-refresh"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-[12px] text-slate-700"
          >
            <ArrowClockwise size={12} />
            Atualizar
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-4">
        <div className="relative max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            data-testid="cidadaos-search"
            placeholder="Buscar por nome, CPF ou telefone…"
            onChange={(e) => debounced(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-md border border-slate-200 text-[13px] bg-white focus:border-emerald-600 focus:outline-none"
          />
        </div>
      </div>

      {/* Tabela */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-[13px]">
          <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-2.5 text-left font-medium">Nome</th>
              <th className="px-4 py-2.5 text-left font-medium">CPF</th>
              <th className="px-4 py-2.5 text-left font-medium">WhatsApp</th>
              <th className="px-4 py-2.5 text-left font-medium">Canal preferido</th>
              <th className="px-4 py-2.5 text-left font-medium">Últ. atendimento</th>
              <th className="px-4 py-2.5 text-right font-medium">Atend.</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400 text-[12px]">Carregando…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-slate-400">
                  <IdentificationCard size={28} className="mx-auto mb-2 text-slate-300" />
                  <div className="text-[13px] font-medium text-slate-500">Nenhum cidadão cadastrado ainda</div>
                  <div className="text-[11.5px] mt-1">Assim que o WhatsApp começar a receber mensagens, os cidadãos aparecerão aqui.</div>
                </td>
              </tr>
            )}
            {!loading && rows.map((c) => (
              <tr
                key={c.id}
                data-testid={`cidadao-row-${c.id}`}
                onClick={() => nav(`/gov/cidadaos/${c.id}`)}
                className="hover:bg-emerald-50/40 cursor-pointer transition-colors"
              >
                <td className="px-4 py-2.5">
                  <div className="font-medium text-slate-900">{c.name || "(sem nome)"}</div>
                  {c.email && (
                    <div className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5">
                      <EnvelopeSimple size={10} /> {c.email}
                    </div>
                  )}
                </td>
                <td className="px-4 py-2.5 text-slate-700 tabular-nums">{c.cpf || "—"}</td>
                <td className="px-4 py-2.5 text-slate-700">
                  {c.phone ? (
                    <div className="flex items-center gap-1"><Phone size={11} className="text-emerald-700" /> {c.phone}</div>
                  ) : "—"}
                </td>
                <td className="px-4 py-2.5">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-800 text-[10.5px] font-medium">
                    WhatsApp
                  </span>
                </td>
                <td className="px-4 py-2.5 text-slate-600 text-[11.5px]">
                  {c.last_seen_at ? new Date(c.last_seen_at).toLocaleDateString("pt-BR") : "—"}
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums text-slate-800 font-medium">
                  {c.conversation_count || 0}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 rounded-lg bg-emerald-50/50 border border-emerald-200 p-3 flex items-start gap-2">
        <ShieldCheck size={16} className="text-emerald-700 shrink-0 mt-0.5" weight="regular" />
        <div className="text-[11.5px] text-emerald-900 leading-relaxed">
          <strong>LGPD Compliant</strong> — cada cidadão pode solicitar acesso, correção ou apagamento dos seus dados via <a href="/gov/lgpd" className="underline">Central LGPD</a>. Todas as operações são auditadas.
        </div>
      </div>
    </div>
  );
}
